import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useParams, useLocation } from "wouter";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { authFetch } from "@/lib/authFetch";
import {
  calcLoyerAuto,
  FINANCEMENT_DUREES,
  FINANCEMENT_COEFFICIENTS,
  hasFinancementCoefficient,
  resolveDurees,
  resolveLoyer,
  resolveMaintenance,
  type DureeFinancement,
} from "@/lib/financement";
import { useAuth } from "@/hooks/use-auth";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  ArrowLeft, Plus, Trash2, Save, Send, FileText, Search, ChevronUp, ChevronDown, Package, Upload, Shield, Wrench, ImageIcon, Copy, Pencil, Check, X, CheckCircle2, PackageCheck, Loader2,
} from "lucide-react";
import type { Devis, FinancementOverrides, LigneDevis, Product } from "@shared/schema";
import { useDevisCart } from "@/contexts/DevisCartContext";
import { SearchSelect } from "@/components/search-select";
import { printDevis } from "./print";

const RECURRENT_TYPES = new Set(["abonnement", "maintenance", "contrat"]);
const CAPITAL_FORCE_TYPES = new Set(["robot", "machine"]);
const FINANCEMENT_DUREES_MULTI = FINANCEMENT_DUREES;

type DestType = "client" | "prospect" | "revendeur";

export type SaveResult =
  | { ok: true; devis: Devis }
  | { ok: false; reason: "network" | "server" | "conflict" | "validation" | "busy" };

interface FormState {
  destinataireType: DestType;
  clientId: string;
  prospectId: string;
  resellerId: string;
  societe: string;
  contactNom: string;
  contactCivilite: string;
  adresse: string;
  objet: string;
  commercialId: string;
  lignes: LigneDevis[];
  remiseGlobale: number;
  modalitesPaiement: string[];
  optionFinancement: boolean;
  dureeFinancementDevis: DureeFinancement;
  dureesFinancement: DureeFinancement[];
  financementOverrides: FinancementOverrides;
  loyerRobotAccessoiresEdite: number | null;
  maintenanceMensuelleEditee: number | null;
  notes: string;
  validiteJours: number;
  numeroOverride: string;
}

function inferDureeDevis(lignes: LigneDevis[]): DureeFinancement {
  const durees = [...new Set(
    lignes
      .filter(l => l.modePaiement === "finance" && l.dureeFinancement && (FINANCEMENT_DUREES as readonly string[]).includes(l.dureeFinancement))
      .map(l => l.dureeFinancement as DureeFinancement)
  )];
  return durees.length === 1 ? durees[0] : "36";
}

const EMPTY_FORM: FormState = {
  destinataireType: "client",
  clientId: "",
  prospectId: "",
  resellerId: "",
  societe: "",
  contactNom: "",
  contactCivilite: "",
  adresse: "",
  objet: "",
  commercialId: "",
  lignes: [],
  remiseGlobale: 0,
  modalitesPaiement: [],
  optionFinancement: false,
  dureeFinancementDevis: "36",
  dureesFinancement: ["36"],
  financementOverrides: {},
  loyerRobotAccessoiresEdite: null,
  maintenanceMensuelleEditee: null,
  notes: "",
  validiteJours: 30,
  numeroOverride: "",
};

function fmt(n: number) {
  return n.toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function calcLigne(l: LigneDevis) {
  const pu = Number(l.puHt) || 0;
  const qty = Number(l.quantite) || 1;
  const rem = Number(l.remise) || 0;
  return pu * qty * (1 - rem / 100);
}

function normalizeMaintenanceQuantity(value: unknown): number {
  const quantity = Number(value);
  return Number.isInteger(quantity) && quantity >= 1 ? quantity : 1;
}

// ── Maintenance helpers ────────────────────────────────────────────────────────
const MAINTENANCE_FIXED_BULLETS = [
  "Hotline et support technique du lundi au vendredi, de 9h00 à 17h30",
  "Dépannage à distance et ouverture ticket d'incident sous 24h (jours ouvrés)",
  "Intervention en 48H00 (jours ouvrés)",
  "Dépannage sur site ou remplacement du robot sous 72h (jours ouvrés)",
  "Mise à disposition d'un robot équivalent durant les réparations",
  "Mises à jour logicielles régulières",
  "1 visite annuelle de maintenance préventive (contrôle mécanique)",
  "Mise à jour des cartographies et paramétrage du robot (1 fois/an – dans la limite d'une ½ journée)",
];

function buildMaintenanceBullets(D: number): string[] {
  return [
    "24 mois de garantie fabricant",
    ...(D > 24 ? [`${D - 24} mois d'extension de garantie inclus`] : []),
    `Garantie pièces (hors consommable) : ${D} mois`,
    ...MAINTENANCE_FIXED_BULLETS,
  ];
}

function buildMaintenanceTitle(D: number): string {
  return `Maintenance - Extension de garantie fabricant (${D} mois / pièces et main d'œuvre)`;
}

function calcLigneEff(l: LigneDevis): number {
  const isComptantMaint =
    (l.modePaiement === "comptant" && l.dureeCouverture !== undefined) ||
    (!l.modePaiement && l.dureeMaintenance === "comptant");
  if (isComptantMaint) {
    const couv = parseInt(l.dureeCouverture || "48");
    const montant = l.montantComptant != null ? Number(l.montantComptant) : (Number(l.puHt) || 0) * couv;
    return montant * (Number(l.quantite) || 1) * (1 - (Number(l.remise) || 0) / 100);
  }
  return calcLigne(l);
}

/** Retourne true si la ligne est récurrente (maintenance mensuelle).
 *  - Nouveau modèle : natureLigne === 'recurrent'
 *  - Fallback anciens devis : dureeCouverture défini (maintenance avec durée de couverture)
 */
function isLigneRecurrente(l: LigneDevis): boolean {
  return l.natureLigne === "recurrent" || (l.dureeCouverture != null && l.dureeCouverture !== "");
}

export default function DevisEdit() {
  const { id } = useParams<{ id: string }>();
  const [, navigate] = useLocation();
  const { profile } = useAuth();
  const { toast } = useToast();
  const qc = useQueryClient();

  const isNew = !id;
  const { items: cartItems, toDevisLignes, clearCart } = useDevisCart();
  const cartInjectedRef = useRef(false);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [productModal, setProductModal] = useState<"produits" | "prestations" | null>(null);
  const [productSearch, setProductSearch] = useState("");
  const [pickerTypeFilter, setPickerTypeFilter] = useState<"robot" | "accessoire" | "consommable" | "iot" | "service" | "all">("robot");
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const generatingPdfRef = useRef(false);
  const [logoUploading, setLogoUploading] = useState(false);
  const [editingNumero, setEditingNumero] = useState(false);
  const [numeroDraft, setNumeroDraft] = useState("");
  const [maintenanceRecalculated, setMaintenanceRecalculated] = useState(false);

  // Fetch existing devis if editing
  const { data: existing, isLoading: loadingExisting } = useQuery<Devis>({
    queryKey: ["/api/devis", id],
    queryFn: async () => {
      const res = await authFetch(`/api/devis/${id}`);
      if (!res.ok) throw new Error("Devis introuvable");
      return res.json();
    },
    enabled: !isNew,
  });

  // Profiles, clients, prospects, resellers, products
  const { data: profiles = [] } = useQuery<any[]>({
    queryKey: ["/api/profiles"],
    queryFn: async () => (await authFetch("/api/profiles")).json(),
  });
  const { data: clients = [] } = useQuery<any[]>({
    queryKey: ["/api/clients"],
    queryFn: async () => (await authFetch("/api/clients")).json(),
  });
  const { data: prospects = [] } = useQuery<any[]>({
    queryKey: ["/api/prospects"],
    queryFn: async () => (await authFetch("/api/prospects")).json(),
  });
  const { data: resellers = [] } = useQuery<any[]>({
    queryKey: ["/api/resellers"],
    queryFn: async () => (await authFetch("/api/resellers")).json(),
  });
  const { data: products = [] } = useQuery<Product[]>({
    queryKey: ["/api/products"],
    queryFn: async () => (await authFetch("/api/products")).json(),
  });
  const { data: logoData, refetch: refetchLogo } = useQuery<{ url: string | null }>({
    queryKey: ["/api/settings/logo"],
    queryFn: async () => (await authFetch("/api/settings/logo")).json(),
  });
  const logoUrl = logoData?.url || null;

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLogoUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await authFetch("/api/settings/logo", { method: "POST", body: fd });
      if (!res.ok) throw new Error(await res.text());
      refetchLogo();
      toast({ title: "Logo mis à jour" });
    } catch (e: any) {
      toast({ title: "Erreur upload logo", description: e.message, variant: "destructive" });
    } finally {
      setLogoUploading(false);
    }
  };

  const commercials = useMemo(
    () => profiles.filter(
      (p: any) => p.isActive !== false && ["admin", "commercial", "planification"].includes(p.role),
    ),
    [profiles]
  );

  // Pre-fill lignes from cart (new devis only)
  useEffect(() => {
    if (!isNew || cartInjectedRef.current || cartItems.length === 0) return;
    cartInjectedRef.current = true;
    const lignes = toDevisLignes();
    setForm(f => ({ ...f, lignes }));
    clearCart();
    toast({
      title: `${lignes.length} produit${lignes.length !== 1 ? "s" : ""} ajouté${lignes.length !== 1 ? "s" : ""} au devis`,
      description: "Les lignes ont été pré-remplies depuis votre sélection.",
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNew, cartItems]);

  // Populate form when existing devis loads
  useEffect(() => {
    if (!existing) return;
    setMaintenanceRecalculated(false);
    const lignes = (Array.isArray(existing.lignes) ? (existing.lignes as unknown as LigneDevis[]) : [])
      .map(l => CAPITAL_FORCE_TYPES.has(l.produitType || "") && l.natureLigne === "recurrent"
        ? { ...l, natureLigne: "capital" as const }
        : l
      );
    const inferredLegacyDuree = inferDureeDevis(lignes);
    const resolvedDurees = resolveDurees({
      ...existing,
      dureeFinancementDevis: existing.dureeFinancementDevis || inferredLegacyDuree,
    })
      .filter((d): d is DureeFinancement =>
        (FINANCEMENT_DUREES as readonly string[]).includes(d)
      );
    const dureesFinancement: DureeFinancement[] = resolvedDurees.length > 0
      ? resolvedDurees
      : [inferredLegacyDuree];
    const dureeFinancementDevis = dureesFinancement[0];
    const financementOverrides: FinancementOverrides =
      existing.financementOverrides && typeof existing.financementOverrides === "object" && !Array.isArray(existing.financementOverrides)
        ? existing.financementOverrides
        : {};
    const destType: DestType = existing.resellerId
      ? "revendeur"
      : existing.prospectId
      ? "prospect"
      : "client";
    setForm({
      destinataireType: destType,
      clientId: existing.clientId || "",
      prospectId: existing.prospectId || "",
      resellerId: existing.resellerId || "",
      societe: existing.societe || "",
      contactNom: existing.contactNom || "",
      contactCivilite: existing.contactCivilite || "",
      adresse: existing.adresse || "",
      objet: existing.objet || "",
      commercialId: existing.commercialId || "",
      lignes,
      remiseGlobale: Number(existing.remiseGlobale) || 0,
      modalitesPaiement: existing.modalitesPaiement || [],
      optionFinancement: existing.optionFinancement || false,
      dureeFinancementDevis,
      dureesFinancement,
      financementOverrides,
      loyerRobotAccessoiresEdite: (existing as any).loyerRobotAccessoiresEdite != null ? Number((existing as any).loyerRobotAccessoiresEdite) : null,
      maintenanceMensuelleEditee: (existing as any).maintenanceMensuelleEditee != null ? Number((existing as any).maintenanceMensuelleEditee) : null,
      notes: existing.notes || "",
      validiteJours: existing.validiteJours || 30,
      numeroOverride: existing.numero || "",
    });
  }, [existing]);

  // Auto-fill contact from selected entity
  const handleEntitySelect = (type: DestType, entityId: string) => {
    if (!entityId) return;
    if (type === "client") {
      const c = clients.find((c: any) => c.id === entityId);
      if (c) {
        setForm(f => ({
          ...f,
          clientId: entityId,
          prospectId: "",
          resellerId: "",
          societe: c.companyName || "",
          contactNom: c.contactName || "",
          contactCivilite: c.contactCivility || "",
          adresse: c.address || "",
        }));
      }
    } else if (type === "prospect") {
      const p = prospects.find((p: any) => p.id === entityId);
      if (p) {
        const nom = [p.prenomContact, p.nomContact].filter(Boolean).join(" ");
        const adr = [p.adresse, p.codePostal, p.ville].filter(Boolean).join(", ");
        setForm(f => ({
          ...f,
          clientId: "",
          prospectId: entityId,
          resellerId: "",
          societe: p.societe || "",
          contactNom: nom,
          contactCivilite: p.civiliteContact || "",
          adresse: adr,
        }));
      }
    } else {
      const r = resellers.find((r: any) => r.id === entityId);
      if (r) {
        const nom = [r.contactFirstName, r.contactLastName].filter(Boolean).join(" ");
        setForm(f => ({
          ...f,
          clientId: "",
          prospectId: "",
          resellerId: entityId,
          societe: r.companyName || "",
          contactNom: nom,
          contactCivilite: r.contactCivility || "",
          adresse: r.address || "",
        }));
      }
    }
  };

  const setField = useCallback(<K extends keyof FormState>(key: K, val: FormState[K]) => {
    setForm(f => ({ ...f, [key]: val }));
  }, []);

  // Add ligne from product
  const addProductToSection = (product: Product, section: "produits" | "prestations") => {
    const isService = product.type === "service";
    const natureLigne: "capital" | "recurrent" = RECURRENT_TYPES.has(product.type || "") ? "recurrent" : "capital";
    const newLine: LigneDevis = {
      id: crypto.randomUUID(),
      section,
      produitId: product.id,
      produitType: product.type || "",
      imageUrl: product.imageUrl || undefined,
      sku: product.sku,
      nomFr: product.nomFr,
      descriptionFr: product.descriptionFr || "",
      puHt: isService ? (Number((product as any).prixClientFinal) || 0) : (Number(product.prixHt) || 0),
      quantite: 1,
      remise: 0,
      modePaiement: "comptant",
      dureeFinancement: null,
      natureLigne,
      ...(isService && (product as any).features?.length > 0 ? { bullets: (product as any).features } : {}),
    };
    setForm(f => ({ ...f, lignes: [...f.lignes, newLine] }));
    setProductModal(null);
  };

  const updateLigne = (id: string, changes: Partial<LigneDevis>) => {
    const currentLine = form.lignes.find(l => l.id === id);
    const requestedQuantity = normalizeMaintenanceQuantity(changes.quantite);
    const isActualMaintenanceQuantityChange =
      currentLine?.dureeMaintenance !== undefined &&
      Object.prototype.hasOwnProperty.call(changes, "quantite") &&
      normalizeMaintenanceQuantity(currentLine.quantite) !== requestedQuantity;
    if (isActualMaintenanceQuantityChange) {
      const activeDurees = form.dureesFinancement.length > 0
        ? form.dureesFinancement
        : [form.dureeFinancementDevis];
      const hadMaintenanceOverride =
        activeDurees.some(duree => form.financementOverrides[duree]?.maintenance != null) ||
        (Object.keys(form.financementOverrides).length === 0 && form.maintenanceMensuelleEditee !== null);
      if (hadMaintenanceOverride) setMaintenanceRecalculated(true);
    }

    setForm(f => {
      const currentLine = f.lignes.find(l => l.id === id);
      const isQuantityChange = Object.prototype.hasOwnProperty.call(changes, "quantite");
      const nextQuantity = currentLine?.dureeMaintenance !== undefined && isQuantityChange
        ? normalizeMaintenanceQuantity(changes.quantite)
        : changes.quantite;
      const isMaintenanceQuantityChange =
        currentLine?.dureeMaintenance !== undefined &&
        isQuantityChange &&
        normalizeMaintenanceQuantity(currentLine.quantite) !== nextQuantity;
      const nextChanges = isQuantityChange
        ? { ...changes, quantite: nextQuantity }
        : changes;

      if (!isMaintenanceQuantityChange) {
        return {
          ...f,
          lignes: f.lignes.map(l => l.id === id ? { ...l, ...nextChanges } : l),
        };
      }

      const nextOverrides = Object.fromEntries(
        Object.entries(f.financementOverrides).map(([duree, override]) => [
          duree,
          override?.maintenance != null
            ? { ...override, maintenance: null }
            : override,
        ])
      );

      return {
        ...f,
        lignes: f.lignes.map(l => l.id === id ? { ...l, ...nextChanges } : l),
        financementOverrides: nextOverrides,
        maintenanceMensuelleEditee: null,
      };
    });
  };

  const removeLigne = (id: string) => {
    setForm(f => ({ ...f, lignes: f.lignes.filter(l => l.id !== id) }));
  };

  const moveLigne = (id: string, dir: -1 | 1) => {
    setForm(f => {
      const idx = f.lignes.findIndex(l => l.id === id);
      if (idx < 0) return f;
      const arr = [...f.lignes];
      const newIdx = idx + dir;
      if (newIdx < 0 || newIdx >= arr.length) return f;
      [arr[idx], arr[newIdx]] = [arr[newIdx], arr[idx]];
      return { ...f, lignes: arr };
    });
  };

  const addMaintenanceBloc = () => {
    const defaultDur = 48;
    const newLine: LigneDevis = {
      id: crypto.randomUUID(),
      section: "prestations",
      nomFr: buildMaintenanceTitle(defaultDur),
      descriptionFr: "",
      bullets: buildMaintenanceBullets(defaultDur),
      puHt: 0,
      quantite: 1,
      remise: 0,
      dureeMaintenance: "48",
      modePaiement: "finance",
      dureeFinancement: "48",
      natureLigne: "recurrent",
      dureeCouverture: undefined,
      montantComptant: null,
    };
    setForm(f => ({ ...f, lignes: [...f.lignes, newLine] }));
  };

  const addInstallationBloc = () => {
    // Cherche le service catalogue SERV-0001 ; fallback hardcodé si absent
    const catalogueService = products.find((p: any) => p.sku === "SERV-0001");
    const INSTALLATION_BULLETS_FALLBACK = [
      "Programmation du robot",
      "Ouverture plateforme magasin pour statistiques et mode",
      "Intervention sur site",
      "Montage / Installation / Calibrage / Mapping site",
      "Programmation des zones",
      "Formation avec l'équipe référente",
    ];
    const newLine: LigneDevis = {
      id: crypto.randomUUID(),
      section: "prestations",
      nomFr: catalogueService ? catalogueService.nomFr : "Forfait Paramétrage / Livraison / Installation / Formation",
      descriptionFr: "",
      modePaiement: "comptant",
      dureeFinancement: null,
      natureLigne: "capital",
      bullets: catalogueService
        ? ((catalogueService as any).features ?? INSTALLATION_BULLETS_FALLBACK)
        : INSTALLATION_BULLETS_FALLBACK,
      puHt: catalogueService ? (Number((catalogueService as any).prixClientFinal) || 0) : 0,
      quantite: 1,
      remise: 0,
      produitId: catalogueService ? catalogueService.id : undefined,
      sku: catalogueService ? catalogueService.sku : undefined,
      imageUrl: catalogueService?.imageUrl ?? undefined,
    };
    setForm(f => ({ ...f, lignes: [...f.lignes, newLine] }));
  };

  const toggleModalite = (m: string) => {
    setForm(f => ({
      ...f,
      modalitesPaiement: f.modalitesPaiement.includes(m)
        ? f.modalitesPaiement.filter(x => x !== m)
        : [...f.modalitesPaiement, m],
    }));
  };

  // Calculated totals
  const lignesProduits = form.lignes.filter(l => l.section === "produits");
  const lignesPrestations = form.lignes.filter(l => l.section === "prestations");
  const sousTotal = form.lignes.filter(l => !isLigneRecurrente(l)).reduce((acc, l) => acc + calcLigneEff(l), 0);
  const totalHT = sousTotal * (1 - form.remiseGlobale / 100);
  const tva = totalHT * 0.2;
  const ttc = totalHT + tva;

  // Phase 1 — base finançable globale (toutes lignes finance, pour rétrocompatibilité)
  const sousTotalFinance = form.lignes.reduce(
    (acc, l) => ((l.modePaiement ?? "comptant") === "finance" ? acc + calcLigneEff(l) : acc),
    0
  );
  const baseFinancee = sousTotalFinance * (1 - form.remiseGlobale / 100);

  // Phase 2 — moteur financement leasing
  const baseCapitalFinance = form.lignes
    .filter(l => (l.natureLigne ?? "capital") === "capital" && l.modePaiement === "finance")
    .reduce((acc, l) => acc + calcLigneEff(l), 0) * (1 - form.remiseGlobale / 100);

  const maintenanceAuto = form.lignes
    .filter(l => l.natureLigne === "recurrent" && l.modePaiement === "finance")
    .reduce((acc, l) => acc + calcLigneEff(l), 0);

  const dureesFinancement = form.dureesFinancement.length > 0
    ? form.dureesFinancement
    : [form.dureeFinancementDevis];
  const firstDureeFinancement = dureesFinancement[0] || "36";
  const financementPreviewDevis = {
    dureesFinancement,
    dureeFinancementDevis: firstDureeFinancement,
    optionFinancement: form.optionFinancement,
    financementOverrides: form.financementOverrides,
    loyerRobotAccessoiresEdite: form.loyerRobotAccessoiresEdite !== null
      ? form.loyerRobotAccessoiresEdite.toString()
      : null,
    maintenanceMensuelleEditee: form.maintenanceMensuelleEditee !== null
      ? form.maintenanceMensuelleEditee.toString()
      : null,
  };
  const financementColumns = dureesFinancement.map(duree => {
    const hasCoefficient = hasFinancementCoefficient(duree);
    const loyerAuto = hasCoefficient ? calcLoyerAuto(baseCapitalFinance, duree) : null;
    const loyer = resolveLoyer(financementPreviewDevis, duree, loyerAuto);
    const maintenance = resolveMaintenance(financementPreviewDevis, duree, maintenanceAuto);
    const totalHT = loyer === null ? null : loyer + maintenance;
    return {
      duree,
      loyer,
      maintenance,
      totalHT,
      tva: totalHT === null ? null : totalHT * 0.2,
      totalTTC: totalHT === null ? null : totalHT * 1.2,
      loyerOverride: form.financementOverrides[duree]?.loyer != null ||
        (Object.keys(form.financementOverrides).length === 0 &&
          dureesFinancement.length === 1 &&
          form.loyerRobotAccessoiresEdite !== null),
    };
  });

  const lignesComptant = form.lignes.filter(
    l => l.modePaiement === "comptant" && (l.natureLigne ?? "capital") === "capital"
  );
  const hasFinanceLines = form.lignes.some(l => l.modePaiement === "finance");

  const maintenanceOverride = dureesFinancement.some(d => form.financementOverrides[d]?.maintenance != null) ||
    (Object.keys(form.financementOverrides).length === 0 &&
      dureesFinancement.length === 1 &&
      form.maintenanceMensuelleEditee !== null);

  const handleDureeFinancementToggle = (duree: DureeFinancement) => {
    setForm(current => {
      const currentDurees = current.dureesFinancement.length > 0
        ? current.dureesFinancement
        : [current.dureeFinancementDevis];
      const isSelected = current.dureesFinancement.includes(duree);
      if (isSelected && currentDurees.length === 1) return current;
      if (!isSelected && currentDurees.length >= 3) return current;

      const nextDurees = isSelected
        ? currentDurees.filter(d => d !== duree)
        : [...currentDurees, duree];

      const currentFinancementDevis = {
        dureesFinancement: currentDurees,
        dureeFinancementDevis: currentDurees[0],
        optionFinancement: current.optionFinancement,
        financementOverrides: current.financementOverrides,
        loyerRobotAccessoiresEdite: current.loyerRobotAccessoiresEdite !== null
          ? current.loyerRobotAccessoiresEdite.toString()
          : null,
        maintenanceMensuelleEditee: current.maintenanceMensuelleEditee !== null
          ? current.maintenanceMensuelleEditee.toString()
          : null,
      };
      const hasMaintenanceOverride = currentDurees.some(d => current.financementOverrides[d]?.maintenance != null) ||
        (Object.keys(current.financementOverrides).length === 0 && current.maintenanceMensuelleEditee !== null);
      const maintenanceSource = currentDurees.find(d => current.financementOverrides[d]?.maintenance != null) || currentDurees[0];
      const maintenanceValue = maintenanceSource
        ? resolveMaintenance(currentFinancementDevis, maintenanceSource, maintenanceAuto)
        : maintenanceAuto;
      const nextOverrides = { ...current.financementOverrides };

      if (Object.keys(current.financementOverrides).length === 0 && current.loyerRobotAccessoiresEdite !== null) {
        const legacyDuree = currentDurees[0];
        nextOverrides[legacyDuree] = {
          ...nextOverrides[legacyDuree],
          loyer: current.loyerRobotAccessoiresEdite,
        };
      }
      if (hasMaintenanceOverride) {
        nextDurees.forEach(d => {
          nextOverrides[d] = { ...nextOverrides[d], maintenance: maintenanceValue };
        });
      } else if (!isSelected && nextOverrides[duree]?.maintenance != null) {
        const reactivatedEntry = { ...nextOverrides[duree] };
        delete reactivatedEntry.maintenance;
        if (Object.keys(reactivatedEntry).length === 0) delete nextOverrides[duree];
        else nextOverrides[duree] = reactivatedEntry;
      }

      return {
        ...current,
        dureesFinancement: nextDurees,
        dureeFinancementDevis: nextDurees[0],
        financementOverrides: nextOverrides,
      };
    });
  };

  const setLoyerOverride = (duree: DureeFinancement, value: number | null) => {
    setForm(current => {
      const activeDurees = current.dureesFinancement.length > 0
        ? current.dureesFinancement
        : [current.dureeFinancementDevis];
      const nextOverrides = { ...current.financementOverrides };
      if (Object.keys(current.financementOverrides).length === 0 && current.maintenanceMensuelleEditee !== null) {
        const firstDuree = activeDurees[0];
        nextOverrides[firstDuree] = {
          ...nextOverrides[firstDuree],
          maintenance: current.maintenanceMensuelleEditee,
        };
      }
      const previous = nextOverrides[duree] || {};
      const nextEntry = { ...previous };
      if (value === null) delete nextEntry.loyer;
      else nextEntry.loyer = value;
      if (Object.keys(nextEntry).length === 0) delete nextOverrides[duree];
      else nextOverrides[duree] = nextEntry;
      return {
        ...current,
        financementOverrides: nextOverrides,
        loyerRobotAccessoiresEdite:
          value === null && duree === activeDurees[0]
            ? null
            : current.loyerRobotAccessoiresEdite,
      };
    });
  };

  const setMaintenanceOverride = (value: number | null) => {
    setMaintenanceRecalculated(false);
    setForm(current => {
      const nextOverrides = { ...current.financementOverrides };
      const activeDurees = current.dureesFinancement.length > 0
        ? current.dureesFinancement
        : [current.dureeFinancementDevis];
      if (Object.keys(current.financementOverrides).length === 0 && current.loyerRobotAccessoiresEdite !== null) {
        const firstDuree = activeDurees[0];
        nextOverrides[firstDuree] = {
          ...nextOverrides[firstDuree],
          loyer: current.loyerRobotAccessoiresEdite,
        };
      }
      activeDurees.forEach(d => {
        const previous = nextOverrides[d] || {};
        const nextEntry = { ...previous };
        if (value === null) delete nextEntry.maintenance;
        else nextEntry.maintenance = value;
        if (Object.keys(nextEntry).length === 0) delete nextOverrides[d];
        else nextOverrides[d] = nextEntry;
      });
      return {
        ...current,
        financementOverrides: nextOverrides,
        maintenanceMensuelleEditee: value === null ? null : current.maintenanceMensuelleEditee,
      };
    });
  };

  // Save
  async function save(statut?: string): Promise<SaveResult> {
    if (savingRef.current) return { ok: false, reason: "busy" };
    savingRef.current = true;
    setSaving(true);
    try {
      const dureesToSave: DureeFinancement[] = form.dureesFinancement.length > 0
        ? form.dureesFinancement
        : [form.dureeFinancementDevis];
      const firstDureeToSave = dureesToSave[0] || "36";
      const overridesToSave: FinancementOverrides = { ...form.financementOverrides };
      const firstOverride = overridesToSave[firstDureeToSave] || {};
      const hasOverridesMap = Object.keys(overridesToSave).length > 0;
      const firstLoyerOverride = firstOverride.loyer != null
        ? firstOverride.loyer
        : !hasOverridesMap ? form.loyerRobotAccessoiresEdite : null;
      const firstMaintenanceOverride = firstOverride.maintenance != null
        ? firstOverride.maintenance
        : !hasOverridesMap ? form.maintenanceMensuelleEditee : null;
      const syncedFirstOverride = { ...firstOverride };
      if (firstLoyerOverride != null) syncedFirstOverride.loyer = firstLoyerOverride;
      else delete syncedFirstOverride.loyer;
      if (firstMaintenanceOverride != null) syncedFirstOverride.maintenance = firstMaintenanceOverride;
      else delete syncedFirstOverride.maintenance;
      if (Object.keys(syncedFirstOverride).length === 0) delete overridesToSave[firstDureeToSave];
      else overridesToSave[firstDureeToSave] = syncedFirstOverride;

      const body: any = {
        statut: statut || (existing?.statut ?? "brouillon"),
        clientId: form.clientId || null,
        prospectId: form.prospectId || null,
        resellerId: form.resellerId || null,
        societe: form.societe || null,
        contactNom: form.contactNom || null,
        contactCivilite: form.contactCivilite || null,
        adresse: form.adresse || null,
        objet: form.objet || null,
        commercialId: form.commercialId || null,
        lignes: form.lignes,
        remiseGlobale: form.remiseGlobale.toString(),
        modalitesPaiement: form.modalitesPaiement,
        optionFinancement: form.optionFinancement,
        dureeFinancementDevis: firstDureeToSave,
        dureesFinancement: dureesToSave,
        financementOverrides: overridesToSave,
        loyerRobotAccessoiresEdite: firstLoyerOverride !== null && firstLoyerOverride !== undefined ? firstLoyerOverride.toString() : null,
        maintenanceMensuelleEditee: firstMaintenanceOverride !== null && firstMaintenanceOverride !== undefined ? firstMaintenanceOverride.toString() : null,
        notes: form.notes || null,
        validiteJours: form.validiteJours,
        ...(!isNew && form.numeroOverride && form.numeroOverride !== existing?.numero
          ? { numero: form.numeroOverride }
          : {}),
      };
      let res: Response;
      if (isNew) {
        res = await authFetch("/api/devis", {
          method: "POST",
          body: JSON.stringify(body),
        });
      } else {
        res = await authFetch(`/api/devis/${id}`, {
          method: "PUT",
          body: JSON.stringify(body),
        });
      }
      if (!res.ok) {
        const errData = await res.json().catch(() => ({ message: res.statusText }));
        if (res.status === 409) {
          toast({ title: errData.message || "Numéro de devis déjà utilisé", variant: "destructive" });
          return { ok: false, reason: "conflict" };
        }
        toast({ title: "Erreur d'enregistrement", description: errData.message || res.statusText, variant: "destructive" });
        return {
          ok: false,
          reason: res.status >= 400 && res.status < 500 ? "validation" : "server",
        };
      }
      const saved: Devis = await res.json();
      qc.invalidateQueries({ queryKey: ["/api/devis"] });
      toast({ title: isNew ? `Devis ${saved.numero} créé` : "Devis enregistré" });
      if (isNew) navigate(`/devis/${saved.id}/editer`);
      return { ok: true, devis: saved };
    } catch (e: any) {
      toast({ title: "Erreur d'enregistrement", description: e.message, variant: "destructive" });
      return { ok: false, reason: "network" };
    } finally {
      setSaving(false);
      savingRef.current = false;
    }
  }

  async function handleGeneratePdf() {
    if (generatingPdfRef.current) return;
    generatingPdfRef.current = true;
    setGeneratingPdf(true);
    try {
      const result = await save();
      if (!result.ok) return;

      if (!Array.isArray(result.devis.lignes)) {
        toast({
          title: "PDF non généré",
          description: "Le devis enregistré ne contient pas de lignes exploitables.",
          variant: "destructive",
        });
        return;
      }

      printDevis(
        result.devis,
        result.devis.lignes as LigneDevis[],
        getCommercialName(result.devis.commercialId || ""),
        logoUrl || undefined,
        getAuteurName(result.devis.createdBy),
      );
    } finally {
      generatingPdfRef.current = false;
      setGeneratingPdf(false);
    }
  }

  const duplicateMutation = useMutation({
    mutationFn: async () => {
      const res = await authFetch(`/api/devis/${id}/duplicate`, { method: "POST" });
      if (!res.ok) throw new Error("Erreur duplication");
      return res.json() as Promise<Devis>;
    },
    onSuccess: (dup: Devis) => {
      qc.invalidateQueries({ queryKey: ["/api/devis"] });
      toast({ title: `Devis dupliqué : ${dup.numero}` });
      navigate(`/devis/${dup.id}/editer`);
    },
    onError: () => toast({ title: "Erreur duplication", variant: "destructive" }),
  });

  const marquerAccepteMutation = useMutation({
    mutationFn: async () => {
      const res = await authFetch(`/api/devis/${id}/marquer-accepte`, { method: "POST" });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || "Erreur");
      }
      return res.json() as Promise<Devis>;
    },
    onSuccess: (updated: Devis) => {
      qc.invalidateQueries({ queryKey: ["/api/devis"] });
      qc.invalidateQueries({ queryKey: ["/api/devis", id] });
      toast({ title: "Devis marqué comme accepté" });
      setForm(f => ({ ...f, statut: updated.statut as any }));
    },
    onError: (e: any) => toast({ title: e?.message || "Erreur", variant: "destructive" }),
  });

  const declarerVenteMutation = useMutation({
    mutationFn: async () => {
      const res = await authFetch(`/api/devis/${id}/declarer-vente`, { method: "POST" });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        const e: any = new Error(err.message || "Erreur");
        e.bonLivraisonId = err.bonLivraisonId;
        throw e;
      }
      return res.json();
    },
    onSuccess: (bl: any) => {
      qc.invalidateQueries({ queryKey: ["/api/devis"] });
      toast({ title: `Vente déclarée : ${bl.numeroBl}` });
      navigate(`/bons-livraison/${bl.id}`);
    },
    onError: (e: any) => {
      if (e?.bonLivraisonId) {
        toast({ title: "Un bon de livraison existe déjà pour ce devis" });
        navigate(`/bons-livraison/${e.bonLivraisonId}`);
      } else {
        toast({ title: e?.message || "Erreur", variant: "destructive" });
      }
    },
  });

  // Reset picker à chaque ouverture
  useEffect(() => {
    if (productModal) {
      setPickerTypeFilter("robot");
      setProductSearch("");
    }
  }, [productModal]);

  const PICKER_TYPE_RANK: Record<string, number> = { robot: 0, accessoire: 1, consommable: 2, iot: 3, service: 4 };

  const filteredProducts = useMemo(() => {
    let active = products.filter(
      p => p.actif && (p as any).statut !== 'a_venir' && (p as any).statut !== 'arrete'
    );
    if (pickerTypeFilter !== "all") {
      active = active.filter(p => p.type === pickerTypeFilter);
    }
    if (productSearch) {
      const s = productSearch.toLowerCase();
      active = active.filter(p =>
        `${p.nomFr} ${p.sku} ${p.descriptionFr || ""}`.toLowerCase().includes(s)
      );
    }
    active.sort((a, b) => {
      const ra = PICKER_TYPE_RANK[a.type] ?? 99;
      const rb = PICKER_TYPE_RANK[b.type] ?? 99;
      if (ra !== rb) return ra - rb;
      return (a.nomFr ?? "").localeCompare(b.nomFr ?? "", "fr");
    });
    return active.slice(0, 50);
  }, [products, productSearch, pickerTypeFilter]);

  const getCommercialName = (cId: string) => {
    const p = profiles.find((p: any) => p.id === cId);
    return p ? `${p.firstName} ${p.lastName}`.trim() : "—";
  };

  // Nom de l'auteur (créateur réel, immuable) — distinct du commercial responsable.
  // Résolu depuis created_by ; absent pour les devis créés avant cette fonctionnalité.
  const getAuteurName = (auteurId: string | null | undefined): string | null => {
    if (!auteurId) return null;
    const p = profiles.find((p: any) => p.id === auteurId);
    return p ? `${p.firstName} ${p.lastName}`.trim() : null;
  };
  const auteurName = getAuteurName((existing as any)?.createdBy);

  if (!isNew && loadingExisting) {
    return (
      <div className="flex items-center justify-center h-64 text-muted-foreground">Chargement…</div>
    );
  }

  // Destinataire options
  const clientOptions = clients.map((c: any) => ({ value: c.id, label: c.companyName, sublabel: c.contactName }));
  const prospectOptions = prospects.map((p: any) => ({ value: p.id, label: p.societe, sublabel: [p.prenomContact, p.nomContact].filter(Boolean).join(" ") }));
  const resellerOptions = resellers.map((r: any) => ({ value: r.id, label: r.companyName }));
  const commercialOptions = commercials.map((p: any) => ({ value: p.id, label: `${p.firstName} ${p.lastName}`.trim() }));

  return (
    <div className="h-full flex flex-col">
      {/* Top bar */}
      <div className="flex items-center justify-between px-6 py-3 border-b bg-card shrink-0">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate("/devis")} data-testid="button-back-devis">
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div>
            <div className="flex items-center gap-2">
              {isNew ? (
                <h1 className="font-bold text-lg">Nouveau devis</h1>
              ) : editingNumero ? (
                <div className="flex items-center gap-1">
                  <Input
                    value={numeroDraft}
                    onChange={e => setNumeroDraft(e.target.value)}
                    className="h-7 w-28 text-sm font-mono"
                    autoFocus
                    onKeyDown={e => {
                      if (e.key === "Enter") { setForm(f => ({ ...f, numeroOverride: numeroDraft })); setEditingNumero(false); }
                      if (e.key === "Escape") setEditingNumero(false);
                    }}
                    data-testid="input-numero-devis"
                  />
                  <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => { setForm(f => ({ ...f, numeroOverride: numeroDraft })); setEditingNumero(false); }} data-testid="button-confirm-numero">
                    <Check className="w-3 h-3 text-green-600" />
                  </Button>
                  <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => setEditingNumero(false)} data-testid="button-cancel-numero">
                    <X className="w-3 h-3 text-red-500" />
                  </Button>
                </div>
              ) : (
                <div className="flex items-center gap-1 group">
                  <h1 className="font-bold text-lg">Devis {form.numeroOverride || existing?.numero || ""}</h1>
                  <Button
                    size="icon" variant="ghost" className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
                    onClick={() => { setNumeroDraft(form.numeroOverride || existing?.numero || ""); setEditingNumero(true); }}
                    data-testid="button-edit-numero"
                    title="Modifier le numéro de devis"
                  >
                    <Pencil className="w-3 h-3 text-muted-foreground" />
                  </Button>
                </div>
              )}
            </div>
            {existing?.statut && (
              <Badge variant="secondary" className="text-xs mt-0.5">
                {existing.statut === "brouillon" ? "Brouillon" :
                 existing.statut === "envoye" ? "Envoyé" :
                 existing.statut === "accepte" ? "Accepté" :
                 existing.statut === "refuse" ? "Refusé" : "Expiré"}
              </Badge>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={handleGeneratePdf}
            disabled={saving || generatingPdf}
            data-testid="button-print-devis"
          >
            {generatingPdf
              ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              : <FileText className="w-4 h-4 mr-2" />}
            {generatingPdf ? "Génération…" : "Générer PDF"}
          </Button>
          {!isNew && (
            <>
              <Button variant="outline" onClick={() => duplicateMutation.mutate()} disabled={duplicateMutation.isPending} data-testid="button-duplicate-devis">
                <Copy className="w-4 h-4 mr-2" /> Dupliquer
              </Button>
              {existing?.statut === "envoye" && (
                <Button
                  variant="outline"
                  onClick={() => marquerAccepteMutation.mutate()}
                  disabled={marquerAccepteMutation.isPending}
                  data-testid="button-marquer-accepte"
                >
                  <CheckCircle2 className="w-4 h-4 mr-2" /> Marquer comme accepté
                </Button>
              )}
              {existing?.statut === "accepte" && (
                <Button
                  onClick={() => declarerVenteMutation.mutate()}
                  disabled={declarerVenteMutation.isPending}
                  data-testid="button-declarer-vente"
                >
                  <PackageCheck className="w-4 h-4 mr-2" /> Déclarer la vente
                </Button>
              )}
            </>
          )}
          <Button variant="outline" onClick={() => save()} disabled={saving} data-testid="button-save-devis">
            <Save className="w-4 h-4 mr-2" /> Enregistrer
          </Button>
          <Button onClick={() => save("envoye")} disabled={saving} data-testid="button-finaliser-devis">
            <Send className="w-4 h-4 mr-2" /> Finaliser
          </Button>
        </div>
      </div>

      {/* Body — two columns */}
      <div className="flex flex-1 overflow-hidden">
        {/* LEFT — Form */}
        <div className="w-[55%] overflow-y-auto p-6 space-y-6 border-r">

          {/* Logo entreprise */}
          <div className="flex items-center gap-4 p-4 bg-secondary/30 rounded-xl border">
            {logoUrl ? (
              <img src={logoUrl} alt="Logo entreprise" className="h-12 max-w-[120px] object-contain rounded" />
            ) : (
              <div className="h-12 w-24 bg-secondary rounded border flex items-center justify-center">
                <ImageIcon className="w-5 h-5 text-muted-foreground" />
              </div>
            )}
            <div className="flex flex-col gap-1">
              <span className="text-sm font-medium">Logo de l'entreprise</span>
              <span className="text-xs text-muted-foreground">Apparaît en haut du devis et du PDF</span>
              <label className="cursor-pointer">
                <input type="file" accept="image/*" className="hidden" onChange={handleLogoUpload} data-testid="input-logo-upload" />
                <span className={`inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md border bg-background hover:bg-secondary transition-colors ${logoUploading ? "opacity-60 pointer-events-none" : ""}`}>
                  <Upload className="w-3 h-3" />
                  {logoUploading ? "Envoi…" : logoUrl ? "Changer" : "Téléverser"}
                </span>
              </label>
            </div>
          </div>

          {/* Bloc destinataire */}
          <section className="space-y-4">
            <h2 className="font-semibold text-base flex items-center gap-2">
              <span className="w-6 h-6 rounded-full bg-primary text-primary-foreground text-xs flex items-center justify-center font-bold">1</span>
              Destinataire
            </h2>

            {/* Type selector */}
            <div className="flex gap-2">
              {(["client", "prospect", "revendeur"] as DestType[]).map(t => (
                <button
                  key={t}
                  type="button"
                  onClick={() => setField("destinataireType", t)}
                  className={`px-4 py-1.5 rounded-full text-sm font-medium border transition-colors ${
                    form.destinataireType === t
                      ? "bg-primary text-primary-foreground border-primary"
                      : "border-border hover:bg-secondary"
                  }`}
                  data-testid={`button-dest-type-${t}`}
                >
                  {t === "client" ? "Client" : t === "prospect" ? "Prospect" : "Revendeur"}
                </button>
              ))}
            </div>

            {/* Entity picker */}
            <div>
              <Label className="text-xs text-muted-foreground mb-1 block">
                {form.destinataireType === "client" ? "Sélectionner un client" :
                 form.destinataireType === "prospect" ? "Sélectionner un prospect" : "Sélectionner un revendeur"}
              </Label>
              <SearchSelect
                value={
                  form.destinataireType === "client" ? form.clientId :
                  form.destinataireType === "prospect" ? form.prospectId : form.resellerId
                }
                onValueChange={v => v && handleEntitySelect(form.destinataireType, v)}
                options={
                  form.destinataireType === "client" ? clientOptions :
                  form.destinataireType === "prospect" ? prospectOptions : resellerOptions
                }
                placeholder="Rechercher…"
                data-testid="select-destinataire"
              />
            </div>

            {/* Manual fields */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-muted-foreground">Société</Label>
                <Input
                  value={form.societe}
                  onChange={e => setField("societe", e.target.value)}
                  placeholder="Nom de la société"
                  data-testid="input-societe"
                />
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Contact</Label>
                <div className="flex gap-1">
                  <Select value={form.contactCivilite} onValueChange={v => setField("contactCivilite", v)}>
                    <SelectTrigger className="w-20" data-testid="select-civilite">
                      <SelectValue placeholder="Civ." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="M.">M.</SelectItem>
                      <SelectItem value="Mme">Mme</SelectItem>
                    </SelectContent>
                  </Select>
                  <Input
                    value={form.contactNom}
                    onChange={e => setField("contactNom", e.target.value)}
                    placeholder="Nom du contact"
                    className="flex-1"
                    data-testid="input-contact-nom"
                  />
                </div>
              </div>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Adresse</Label>
              <Textarea
                value={form.adresse}
                onChange={e => setField("adresse", e.target.value)}
                placeholder="Adresse complète"
                rows={2}
                data-testid="input-adresse"
              />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Commercial responsable</Label>
              <SearchSelect
                value={form.commercialId}
                onValueChange={v => setField("commercialId", v || "")}
                options={commercialOptions}
                placeholder="Sélectionner un commercial"
                data-testid="select-commercial"
              />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Objet de la demande</Label>
              <Input
                value={form.objet}
                onChange={e => setField("objet", e.target.value)}
                placeholder="Ex: Fourniture et installation BellaBot Pro"
                data-testid="input-objet"
              />
            </div>
          </section>

          {/* Bloc lignes — section Produits & équipements */}
          <section className="space-y-3">
            <h2 className="font-semibold text-base flex items-center gap-2">
              <span className="w-6 h-6 rounded-full bg-primary text-primary-foreground text-xs flex items-center justify-center font-bold">2</span>
              Produits & équipements
            </h2>
            <LignesSection
              lignes={lignesProduits}
              section="produits"
              onUpdate={updateLigne}
              onRemove={removeLigne}
              onMove={moveLigne}
              onAdd={() => setProductModal("produits")}
            />
          </section>

          {/* Bloc lignes — section Prestations */}
          <section className="space-y-3">
            <h2 className="font-semibold text-base flex items-center gap-2">
              <span className="w-6 h-6 rounded-full bg-primary text-primary-foreground text-xs flex items-center justify-center font-bold">3</span>
              Prestations à la commande
            </h2>
            <LignesSection
              lignes={lignesPrestations}
              section="prestations"
              onUpdate={updateLigne}
              onRemove={removeLigne}
              onMove={moveLigne}
              onAdd={() => setProductModal("prestations")}
              onAddMaintenance={addMaintenanceBloc}
              onAddInstallation={addInstallationBloc}
            />
          </section>

          {/* Récapitulatif */}
          <section className="space-y-3">
            <h2 className="font-semibold text-base flex items-center gap-2">
              <span className="w-6 h-6 rounded-full bg-primary text-primary-foreground text-xs flex items-center justify-center font-bold">4</span>
              Récapitulatif
            </h2>
            <div className="bg-secondary/30 rounded-xl p-4 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Sous-total HT</span>
                <span className="font-medium tabular-nums">{fmt(sousTotal)} €</span>
              </div>
              <div className="flex items-center gap-3">
                <Label className="text-sm text-muted-foreground whitespace-nowrap">Remise globale (%)</Label>
                <Input
                  type="number"
                  min={0}
                  max={100}
                  step={0.5}
                  value={form.remiseGlobale || ""}
                  onChange={e => setField("remiseGlobale", Number(e.target.value))}
                  className="h-8 w-24 text-right tabular-nums"
                  data-testid="input-remise-globale"
                />
              </div>
              {form.remiseGlobale > 0 && (
                <div className="flex justify-between text-sm text-red-600">
                  <span>Remise ({form.remiseGlobale}%)</span>
                  <span className="tabular-nums">-{fmt(sousTotal - totalHT)} €</span>
                </div>
              )}
              <div className="flex justify-between text-base font-bold bg-primary text-primary-foreground rounded-lg px-3 py-2 mt-1">
                <span>Total HT</span>
                <span className="tabular-nums">{fmt(totalHT)} €</span>
              </div>
              <div className="flex justify-between text-sm text-muted-foreground">
                <span>TVA 20%</span>
                <span className="tabular-nums">{fmt(tva)} €</span>
              </div>
              <div className="flex justify-between text-sm text-muted-foreground">
                <span>Net TTC</span>
                <span className="tabular-nums">{fmt(ttc)} €</span>
              </div>
            </div>
          </section>

          {/* Options */}
          <section className="space-y-4">
            <h2 className="font-semibold text-base flex items-center gap-2">
              <span className="w-6 h-6 rounded-full bg-primary text-primary-foreground text-xs flex items-center justify-center font-bold">5</span>
              Options de paiement &amp; financement
            </h2>
            <div className="space-y-2">
              {["Comptant", "Virement bancaire"].map(m => (
                <label key={m} className="flex items-center gap-3 cursor-pointer hover:bg-secondary/30 rounded-lg p-2 transition-colors">
                  <Checkbox
                    checked={form.modalitesPaiement.includes(m)}
                    onCheckedChange={() => toggleModalite(m)}
                    id={`modalite-${m}`}
                    data-testid={`checkbox-modalite-${m}`}
                  />
                  <span className="text-sm">{m}</span>
                </label>
              ))}
            </div>
            {hasFinanceLines && (
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Switch
                    checked={form.optionFinancement}
                    onCheckedChange={v => setField("optionFinancement", v)}
                    data-testid="switch-option-financement"
                  />
                  <span>Inclure le tableau de financement dans le devis</span>
                </div>
                {form.optionFinancement && (
                  <div className="flex flex-wrap items-center gap-2 ml-9">
                    <span className="text-xs text-muted-foreground whitespace-nowrap">Durées de financement :</span>
                    {FINANCEMENT_DUREES_MULTI.map(duree => {
                      const selected = form.dureesFinancement.includes(duree);
                      const disabled = !selected && form.dureesFinancement.length >= 3;
                      return (
                        <button
                          key={duree}
                          type="button"
                          onClick={() => handleDureeFinancementToggle(duree)}
                          disabled={disabled}
                          title={disabled ? "3 durées maximum" : undefined}
                          className={`h-8 rounded-full border px-3 text-xs font-semibold transition-colors ${
                            selected
                              ? "border-primary bg-primary text-primary-foreground"
                              : disabled
                                ? "border-border bg-muted text-muted-foreground/50 cursor-not-allowed"
                                : "border-border bg-background text-muted-foreground hover:border-primary/60 hover:text-foreground"
                          }`}
                          aria-pressed={selected}
                          data-testid={`button-duree-financement-${duree}`}
                        >
                          {duree} mois
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </section>

          {/* Notes + validité */}
          <section className="space-y-3">
            <h2 className="font-semibold text-base flex items-center gap-2">
              <span className="w-6 h-6 rounded-full bg-primary text-primary-foreground text-xs flex items-center justify-center font-bold">6</span>
              Notes &amp; conditions
            </h2>
            <div>
              <Label className="text-xs text-muted-foreground">Notes / conditions particulières</Label>
              <Textarea
                value={form.notes}
                onChange={e => setField("notes", e.target.value)}
                placeholder="Conditions de livraison, remarques particulières…"
                rows={3}
                data-testid="textarea-notes"
              />
            </div>
            <div className="flex items-center gap-3">
              <Label className="text-sm whitespace-nowrap">Durée de validité</Label>
              <Input
                type="number"
                min={1}
                max={365}
                value={form.validiteJours}
                onChange={e => setField("validiteJours", Number(e.target.value))}
                className="h-8 w-20 tabular-nums"
                data-testid="input-validite-jours"
              />
              <span className="text-sm text-muted-foreground">jours</span>
            </div>
          </section>
        </div>

        {/* RIGHT — Live preview */}
        <div className="w-[45%] overflow-y-auto bg-muted/30 p-6">
          <div className="bg-white rounded-xl border shadow-sm p-6 min-h-full text-[13px]">
            {/* Header */}
            <div className="flex justify-between items-start mb-6 pb-4 border-b-2">
              <div>
                {logoUrl ? (
                  <img src={logoUrl} alt="Logo" className="h-10 max-w-[120px] object-contain mb-1" />
                ) : (
                  <div className="text-2xl font-black tracking-tight">JOBOTECH</div>
                )}
              </div>
              <div className="text-right">
                <div className="text-xl font-bold text-blue-600">DEVIS</div>
                {existing?.numero && <div className="font-semibold">N° {existing.numero}</div>}
                <div className="text-xs text-muted-foreground">
                  {new Date().toLocaleDateString("fr-FR")} · Validité : {form.validiteJours} j
                </div>
                {form.commercialId && (
                  <div className="text-xs text-muted-foreground">Commercial : {getCommercialName(form.commercialId)}</div>
                )}
                {auteurName && (
                  <div className="text-xs text-muted-foreground">Auteur : {auteurName}</div>
                )}
              </div>
            </div>

            {/* Destinataire */}
            {(form.societe || form.contactNom) && (
              <div className="bg-slate-50 border rounded-lg p-3 mb-4">
                <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1">Destinataire</div>
                {form.societe && <div className="font-bold">{form.societe}</div>}
                {form.contactNom && <div className="text-muted-foreground text-xs">{form.contactCivilite} {form.contactNom}</div>}
                {form.adresse && <div className="text-xs text-muted-foreground mt-1 whitespace-pre-line">{form.adresse}</div>}
              </div>
            )}

            {/* Objet */}
            {form.objet && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                <div className="text-[10px] font-bold uppercase tracking-wider text-blue-500 mb-1">Objet</div>
                <div className="font-semibold text-blue-800 text-sm">{form.objet}</div>
              </div>
            )}

            {/* Produits */}
            {lignesProduits.length > 0 && (
              <div className="mb-4">
                <div className="bg-slate-800 text-white text-[10px] font-bold uppercase tracking-wider px-3 py-2 rounded-t-lg">
                  Produits &amp; équipements
                </div>
                <div className="border border-t-0 rounded-b-lg overflow-hidden">
                  {lignesProduits.map(l => {
                    const isCapitalFinance = l.modePaiement === "finance" && (l.natureLigne ?? "capital") === "capital" && l.dureeFinancement;
                    const mensualiteIndicative = isCapitalFinance
                      ? calcLigne(l) * (FINANCEMENT_COEFFICIENTS[l.dureeFinancement!] ?? 0) / 100
                      : null;
                    return (
                      <div key={l.id} className="flex gap-2 p-2 border-b last:border-0 items-center">
                        {l.imageUrl ? (
                          <img src={l.imageUrl} className="w-9 h-9 rounded object-cover border shrink-0" />
                        ) : (
                          <div className="w-9 h-9 rounded bg-slate-100 border shrink-0 flex items-center justify-center">
                            <Package className="w-3.5 h-3.5 text-muted-foreground" />
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="text-[11px] font-mono text-muted-foreground">{l.sku}</div>
                          <div className="font-semibold text-[12px] truncate">{l.nomFr}</div>
                          {l.descriptionFr && (
                            <div className="text-[10px] text-muted-foreground whitespace-pre-wrap break-words">{l.descriptionFr}</div>
                          )}
                        </div>
                        <div className="text-right shrink-0">
                          {Number(l.remise) > 0 ? (
                            <>
                              <div className="text-[10px] line-through text-muted-foreground">{fmt(Number(l.puHt))} €</div>
                              <div className="text-xs font-bold">{fmt(calcLigne(l))} €</div>
                            </>
                          ) : (
                            <div className="text-xs font-semibold">{fmt(calcLigne(l))} €</div>
                          )}
                          {mensualiteIndicative !== null && (
                            <div className="text-[10px] text-blue-600 font-medium">~{fmt(mensualiteIndicative)} €/mois*</div>
                          )}
                          <div className="text-[10px] text-muted-foreground">×{l.quantite}</div>
                        </div>
                      </div>
                    );
                  })}
                  {lignesProduits.some(l => l.modePaiement === "finance" && (l.natureLigne ?? "capital") === "capital") && (
                    <div className="px-3 py-1.5 text-[9px] text-muted-foreground italic border-t">
                      *Montant mensuel indicatif. Le loyer contractuel figure dans le tableau de financement.
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Prestations */}
            {lignesPrestations.length > 0 && (
              <div className="mb-4">
                <div className="bg-slate-800 text-white text-[10px] font-bold uppercase tracking-wider px-3 py-2 rounded-t-lg">
                  Prestations à la commande
                </div>
                <div className="border border-t-0 rounded-b-lg overflow-hidden">
                  {lignesPrestations.map(l => {
                    const isFinancedMaintenance = l.dureeMaintenance !== undefined && l.modePaiement === "finance";
                    const quantity = normalizeMaintenanceQuantity(l.quantite);
                    return (
                    <div key={l.id} className="p-2 border-b last:border-0">
                      <div className="flex gap-2 items-start">
                        <div className="flex-1 min-w-0">
                          <div className="font-semibold text-[12px]">{l.nomFr}</div>
                          {l.precision && (
                            <div className="text-[10px] text-muted-foreground whitespace-pre-wrap break-words">{l.precision}</div>
                          )}
                          {l.descriptionFr && (
                            <div className="text-[10px] text-muted-foreground whitespace-pre-wrap break-words">{l.descriptionFr}</div>
                          )}
                          {Array.isArray(l.bullets) && l.bullets.length > 0 && (
                            <ul className="mt-1 space-y-0.5 pl-1">
                              {l.bullets.map((b, bi) => (
                                <li key={bi} className="flex items-start gap-1 text-[9px] text-muted-foreground">
                                  <span className="text-primary shrink-0 mt-0.5">•</span>
                                  <span>{b}</span>
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                        <div className="text-right text-xs font-semibold shrink-0 ml-2">
                          {(l.natureLigne === "recurrent" && (l.modePaiement === "finance" || (!l.modePaiement && l.dureeMaintenance && l.dureeMaintenance !== "comptant")))
                            ? isFinancedMaintenance && quantity > 1
                              ? `${fmt(Number(l.puHt))} €/mois × ${quantity} = ${fmt(calcLigneEff(l))} €/mois`
                              : `${fmt(Number(l.puHt))} €/mois`
                            : `${fmt(calcLigneEff(l))} €`}
                        </div>
                      </div>
                    </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Totaux */}
            {form.lignes.length > 0 && (
              <div className="flex justify-end mb-4">
                <div className="min-w-[200px] space-y-1">
                  <div className="flex justify-between text-xs text-muted-foreground">
                    <span>Sous-total HT</span>
                    <span className="tabular-nums">{fmt(sousTotal)} €</span>
                  </div>
                  {form.remiseGlobale > 0 && (
                    <div className="flex justify-between text-xs text-red-600">
                      <span>Remise -{form.remiseGlobale}%</span>
                      <span className="tabular-nums">-{fmt(sousTotal - totalHT)} €</span>
                    </div>
                  )}
                  <div className="flex items-center justify-between gap-4 text-base font-bold bg-slate-800 text-white rounded-lg px-4 py-3 mt-1">
                    <span>Total HT</span>
                    <span className="text-lg font-extrabold tabular-nums">{fmt(totalHT)} €</span>
                  </div>
                  <div className="flex justify-between text-xs text-muted-foreground">
                    <span>TVA 20%</span>
                    <span className="tabular-nums">{fmt(tva)} €</span>
                  </div>
                  <div className="flex justify-between text-xs text-muted-foreground">
                    <span>Net TTC</span>
                    <span className="tabular-nums">{fmt(ttc)} €</span>
                  </div>
                </div>
              </div>
            )}

            {/* Financement — OPTION FINANCEMENT tableau Phase 2 */}
            {form.optionFinancement && (
              <div className="mb-4">
                {!hasFinanceLines ? (
                  <div className="border border-amber-200 bg-amber-50 dark:bg-amber-950/20 dark:border-amber-800 rounded-lg p-3 text-xs text-amber-700 dark:text-amber-400">
                    Aucune ligne financée — cochez le financement sur au moins une ligne pour afficher le tableau.
                  </div>
                ) : (
                  <div className="border border-border rounded-lg overflow-hidden text-xs">
                    {/* En-tête */}
                    <div className="bg-slate-800 text-white px-3 py-2 font-bold tracking-wider uppercase text-[10px]">
                      OPTION FINANCEMENT
                    </div>
                    {/* Bloc comptant */}
                    {lignesComptant.length > 0 && (
                      <div className="px-3 py-2 border-b border-border/60 bg-muted/20 space-y-0.5">
                        <div className="font-semibold text-[11px] text-muted-foreground mb-1">Paiement à livraison</div>
                        {lignesComptant.map(l => (
                          <div key={l.id} className="flex justify-between">
                            <span className="text-muted-foreground">{l.nomFr?.trim() || l.designation?.trim() || "Article"} :</span>
                            <span className="font-medium tabular-nums">{fmt(calcLigneEff(l))} € HT</span>
                          </div>
                        ))}
                      </div>
                    )}
                    {/* Tableau loyer */}
                    <div className="overflow-x-auto">
                    <table
                      className="w-full"
                      style={{ minWidth: `${220 + financementColumns.length * 110}px` }}
                    >
                      <thead>
                        <tr className="bg-primary/10">
                          <th className="px-3 py-2 text-left font-semibold text-muted-foreground">DURÉE</th>
                          {financementColumns.map(column => (
                            <th key={column.duree} className="px-3 py-2 text-right font-bold whitespace-nowrap">
                              {column.duree} MOIS
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-border/40">
                        <tr>
                          <td className="px-3 py-2 text-muted-foreground">
                            Financement robot et accessoires
                          </td>
                          {financementColumns.map(column => (
                            <td key={column.duree} className="px-3 py-2 text-right font-semibold tabular-nums whitespace-nowrap">
                              <div className="flex items-center justify-end gap-1">
                                {column.loyerOverride && (
                                  <span className="text-[9px] px-1 py-0.5 rounded bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 font-medium" title="Valeur ajustée manuellement">ajusté</span>
                                )}
                                {column.loyer === null ? (
                                  <span title="Aucune valeur d'origine enregistrée">—</span>
                                ) : (
                                  <>
                                    <input
                                      type="number"
                                      step="0.01"
                                      data-testid={`input-loyer-robot-${column.duree}`}
                                      value={column.loyer.toFixed(2)}
                                      onChange={e => {
                                        const value = parseFloat(e.target.value);
                                        setLoyerOverride(column.duree, isNaN(value) ? null : value);
                                      }}
                                      onDoubleClick={() => setLoyerOverride(column.duree, null)}
                                      className="w-20 text-right bg-transparent border-b border-dashed border-muted-foreground/40 focus:outline-none focus:border-primary tabular-nums"
                                      title="Double-clic pour réinitialiser au calcul auto"
                                    /> €
                                  </>
                                )}
                              </div>
                            </td>
                          ))}
                        </tr>
                        <tr>
                          <td className="px-3 py-2 text-muted-foreground">
                            Maintenance Mensuelle
                            {maintenanceOverride && (
                              <span className="ml-1 text-[9px] px-1 py-0.5 rounded bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 font-medium" title="Valeur ajustée manuellement">ajusté</span>
                            )}
                            {!maintenanceOverride && maintenanceRecalculated && (
                              <span className="ml-1 text-[9px] px-1 py-0.5 rounded bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 font-medium" title="Override invalidé après modification de la quantité">recalculé</span>
                            )}
                          </td>
                          {financementColumns.map((column, index) => (
                            <td key={column.duree} className="px-3 py-2 text-right font-semibold tabular-nums whitespace-nowrap">
                              {index === 0 ? (
                                <>
                                  <input
                                    type="number"
                                    step="0.01"
                                    data-testid="input-maintenance-mensuelle"
                                    value={column.maintenance.toFixed(2)}
                                    onChange={e => {
                                      const value = parseFloat(e.target.value);
                                      setMaintenanceOverride(isNaN(value) ? null : value);
                                    }}
                                    onDoubleClick={() => setMaintenanceOverride(null)}
                                    className="w-20 text-right bg-transparent border-b border-dashed border-muted-foreground/40 focus:outline-none focus:border-primary tabular-nums"
                                    title="Double-clic pour réinitialiser au calcul auto"
                                  /> €
                                </>
                              ) : (
                                <span>{fmt(column.maintenance)} €</span>
                              )}
                            </td>
                          ))}
                        </tr>
                        <tr className="bg-slate-800 text-white font-extrabold">
                          <td className="px-3 py-2">TOTAL MENSUEL HT</td>
                          {financementColumns.map(column => (
                            <td key={column.duree} className="px-3 py-2 text-right tabular-nums whitespace-nowrap">
                              {column.totalHT === null ? "—" : `${fmt(column.totalHT)} €`}
                            </td>
                          ))}
                        </tr>
                        <tr className="text-muted-foreground">
                          <td className="px-3 py-2">TVA (20 %)</td>
                          {financementColumns.map(column => (
                            <td key={column.duree} className="px-3 py-2 text-right tabular-nums whitespace-nowrap">
                              {column.tva === null ? "—" : `${fmt(column.tva)} €`}
                            </td>
                          ))}
                        </tr>
                        <tr className="text-muted-foreground">
                          <td className="px-3 py-2">TOTAL MENSUEL TTC</td>
                          {financementColumns.map(column => (
                            <td key={column.duree} className="px-3 py-2 text-right tabular-nums whitespace-nowrap">
                              {column.totalTTC === null ? "—" : `${fmt(column.totalTTC)} €`}
                            </td>
                          ))}
                        </tr>
                      </tbody>
                    </table>
                    </div>
                    <div className="px-3 py-1.5 text-[9px] text-muted-foreground/70 bg-muted/10">
                      Double-clic sur un montant pour revenir au calcul automatique.
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Notes */}
            {form.notes && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-xs text-yellow-800">
                <div className="font-bold text-[10px] uppercase tracking-wider text-yellow-600 mb-1">Notes</div>
                <div className="whitespace-pre-line">{form.notes}</div>
              </div>
            )}

            {/* Pied de devis — RIB fixe */}
            <div className="mt-4 pt-4 border-t space-y-2">
              <div className="bg-slate-50 border rounded-lg p-3 text-[10px] text-slate-700 space-y-1">
                <div className="font-bold uppercase tracking-wider text-slate-500 mb-1.5">RIB</div>
                <div><span className="font-semibold">Établissement :</span> QONTO</div>
                <div><span className="font-semibold">IBAN :</span> FR76 1695 8000 0182 7628 3540 174</div>
                <div><span className="font-semibold">BIC :</span> QNTOFRP1XXX</div>
              </div>
              <div className="text-[9px] text-slate-500 leading-relaxed">
                Merci d'inclure la référence du devis dans le libellé de votre virement. Pénalités de retard : trois fois le taux annuel d'intérêt légal en vigueur depuis la date d'échéance jusqu'à complet paiement. Indemnité forfaitaire pour frais de recouvrement en cas de retard de paiement : 40 €. Devis gratuit. Validité 1 mois. Délais de livraison : selon stock disponible, sinon 8 à 10 semaines à réception du règlement.
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Product search modal */}
      <Dialog open={!!productModal} onOpenChange={o => !o && setProductModal(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>
              Ajouter un produit — {productModal === "produits" ? "Produits & équipements" : "Prestations à la commande"}
            </DialogTitle>
          </DialogHeader>
          <div className="relative mb-2 shrink-0">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              autoFocus
              placeholder="Rechercher par nom ou SKU…"
              value={productSearch}
              onChange={e => setProductSearch(e.target.value)}
              className="pl-9"
              data-testid="input-product-search"
            />
          </div>
          {/* Segment filtre par type */}
          <div className="flex gap-1 mb-3 shrink-0 flex-wrap">
            {([ ["robot", "Robots"], ["accessoire", "Accessoires"], ["consommable", "Consommables"], ["iot", "IoT"], ["service", "Services"], ["all", "Tous"] ] as const).map(([val, label]) => (
              <button
                key={val}
                type="button"
                data-testid={`picker-type-${val}`}
                onClick={() => setPickerTypeFilter(val)}
                className={`px-3 py-1 rounded-full text-xs font-medium transition-colors border ${
                  pickerTypeFilter === val
                    ? "bg-primary text-primary-foreground border-primary"
                    : "bg-transparent text-muted-foreground border-border hover:bg-secondary/60"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
          <div className="flex-1 overflow-y-auto space-y-1">
            {filteredProducts.map(p => (
              <button
                key={p.id}
                type="button"
                className="w-full flex items-center gap-3 p-3 rounded-lg hover:bg-secondary/50 transition-colors text-left border border-transparent hover:border-border"
                onClick={() => productModal && addProductToSection(p, productModal)}
                data-testid={`button-add-product-${p.id}`}
              >
                {p.imageUrl ? (
                  <img src={p.imageUrl} className="w-12 h-12 rounded-lg object-cover border shrink-0" />
                ) : (
                  <div className="w-12 h-12 rounded-lg bg-secondary border flex items-center justify-center shrink-0">
                    <Package className="w-5 h-5 text-muted-foreground" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="text-xs text-muted-foreground font-mono">{p.sku}</div>
                  <div className="font-semibold text-sm">{p.nomFr}</div>
                  {p.descriptionFr && <div className="text-xs text-muted-foreground truncate">{p.descriptionFr}</div>}
                </div>
                <div className="text-right shrink-0">
                  <div className="font-semibold tabular-nums text-sm">
                    {p.prixHt ? `${Number(p.prixHt).toFixed(2)} €` : "—"}
                  </div>
                  <div className="text-xs text-muted-foreground">HT</div>
                </div>
              </button>
            ))}
            {filteredProducts.length === 0 && (
              <div className="text-center py-10 text-muted-foreground text-sm">
                {pickerTypeFilter !== "all"
                  ? <>
                      Aucun{" "}
                      {{ robot: "robot", accessoire: "accessoire", consommable: "consommable", iot: "produit IoT" }[pickerTypeFilter]}{" "}
                      {productSearch ? "ne correspond à votre recherche" : "disponible"}.
                      <br />
                      <span className="text-xs opacity-70">Essayez le filtre <strong>Tous</strong> pour élargir.</span>
                    </>
                  : "Aucun produit ne correspond à votre recherche."
                }
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ── MaintenanceLigneCard ───────────────────────────────────────────────────────

interface MaintenanceLigneCardProps {
  l: LigneDevis;
  idx: number;
  total: number;
  onUpdate: (id: string, c: Partial<LigneDevis>) => void;
  onRemove: (id: string) => void;
  onMove: (id: string, dir: -1 | 1) => void;
}

function MaintenanceLigneCard({ l, idx, total, onUpdate, onRemove, onMove }: MaintenanceLigneCardProps) {
  const isComptant = l.dureeMaintenance === "comptant";
  const couv = l.dureeCouverture || "48";
  const effectiveTotal = calcLigneEff(l);

  const handleDureeChange = (newDuree: string) => {
    if (newDuree === "comptant") {
      const D = parseInt(couv);
      onUpdate(l.id, {
        dureeMaintenance: "comptant",
        modePaiement: "comptant",
        dureeFinancement: null,
        dureeCouverture: couv,
        nomFr: buildMaintenanceTitle(D),
        bullets: buildMaintenanceBullets(D),
        montantComptant: l.montantComptant != null ? l.montantComptant : (((Number(l.puHt) || 0) * D) || null),
      });
    } else {
      const D = parseInt(newDuree);
      onUpdate(l.id, {
        dureeMaintenance: newDuree,
        modePaiement: "finance",
        dureeFinancement: newDuree as DureeFinancement,
        nomFr: buildMaintenanceTitle(D),
        bullets: buildMaintenanceBullets(D),
        montantComptant: null,
      });
    }
  };

  const handleCouvertureChange = (newCouv: string) => {
    const D = parseInt(newCouv);
    onUpdate(l.id, {
      dureeCouverture: newCouv,
      nomFr: buildMaintenanceTitle(D),
      bullets: buildMaintenanceBullets(D),
      montantComptant: (Number(l.puHt) || 0) * D,
    });
  };

  const handleMensuelChange = (val: number) => {
    const D = parseInt(couv);
    onUpdate(l.id, {
      puHt: val,
      montantComptant: isComptant ? val * D : null,
    });
  };
  const quantity = normalizeMaintenanceQuantity(l.quantite);

  return (
    <div className="border border-blue-200 rounded-xl p-3 bg-blue-50/30 dark:bg-blue-950/10 space-y-2">
      <div className="flex gap-2 items-start">
        {/* Badge maintenance */}
        <div className="w-10 h-10 rounded-lg bg-blue-100 border border-blue-200 flex items-center justify-center shrink-0 mt-0.5">
          <Shield className="w-4 h-4 text-blue-600" />
        </div>

        <div className="flex-1 min-w-0 space-y-2">
          {/* Sélecteurs durée */}
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs font-semibold text-blue-700 shrink-0">Durée</span>
            <Select value={l.dureeMaintenance || "48"} onValueChange={handleDureeChange}>
              <SelectTrigger className="h-7 w-32 text-xs" data-testid={`select-duree-maint-${l.id}`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="comptant">Comptant</SelectItem>
                {FINANCEMENT_DUREES.map(d => (
                  <SelectItem key={d} value={d}>{d} mois</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {isComptant && (
              <>
                <span className="text-xs text-muted-foreground shrink-0">Couverture</span>
                <Select value={couv} onValueChange={handleCouvertureChange}>
                  <SelectTrigger className="h-7 w-28 text-xs" data-testid={`select-couverture-maint-${l.id}`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {FINANCEMENT_DUREES.map(d => (
                      <SelectItem key={d} value={d}>{d} mois</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </>
            )}
          </div>

          {/* Titre auto-généré */}
          <div className="text-[12px] font-semibold text-foreground">{l.nomFr}</div>
          {l.precision && (
            <div className="text-[11px] text-muted-foreground whitespace-pre-wrap break-words">{l.precision}</div>
          )}
          {l.descriptionFr && (
            <div className="text-[11px] text-muted-foreground whitespace-pre-wrap break-words">{l.descriptionFr}</div>
          )}

          {/* Bullets */}
          {Array.isArray(l.bullets) && l.bullets.length > 0 && (
            <div className="pl-1 space-y-0.5">
              {l.bullets.map((b, bi) => (
                <div key={bi} className="flex items-start gap-1 text-[11px] text-muted-foreground">
                  <span className="mt-0.5 text-blue-500 shrink-0">•</span>
                  <span>{b}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Controls */}
        <div className="flex flex-col gap-1 shrink-0">
          <button type="button" onClick={() => onMove(l.id, -1)} disabled={idx === 0} className="p-1 rounded hover:bg-secondary disabled:opacity-30">
            <ChevronUp className="w-3 h-3" />
          </button>
          <button type="button" onClick={() => onMove(l.id, 1)} disabled={idx === total - 1} className="p-1 rounded hover:bg-secondary disabled:opacity-30">
            <ChevronDown className="w-3 h-3" />
          </button>
          <button type="button" onClick={() => onRemove(l.id)} className="p-1 rounded hover:bg-red-100 text-red-500">
            <Trash2 className="w-3 h-3" />
          </button>
        </div>
      </div>

      {/* Prix row */}
      <div className="flex flex-wrap gap-2 items-center text-xs pl-12">
        <Label className="text-muted-foreground whitespace-nowrap shrink-0">
          {isComptant ? "Mensualité réf." : "Mensualité HT"}
        </Label>
        <Input
          type="number"
          min={0}
          step={0.01}
          value={l.puHt || ""}
          onChange={e => handleMensuelChange(Number(e.target.value))}
          className="h-7 w-24 tabular-nums text-right"
          data-testid={`input-pu-maint-${l.id}`}
        />
        <span className="text-muted-foreground">€/mois</span>
        <Label className="text-muted-foreground whitespace-nowrap shrink-0 ml-2">Qté</Label>
        <Input
          type="number"
          min={1}
          step={1}
          value={quantity}
          onChange={e => onUpdate(l.id, { quantite: normalizeMaintenanceQuantity(e.target.value) })}
          className="h-7 w-16 tabular-nums text-center"
          data-testid={`input-qty-maint-${l.id}`}
        />

        {isComptant && (
          <>
            <Label className="text-muted-foreground whitespace-nowrap shrink-0 ml-2">Total global</Label>
            <Input
              type="number"
              min={0}
              step={0.01}
              value={l.montantComptant != null ? l.montantComptant : ((Number(l.puHt) || 0) * parseInt(couv)) || ""}
              onChange={e => onUpdate(l.id, { montantComptant: Number(e.target.value) })}
              className="h-7 w-28 tabular-nums text-right"
              data-testid={`input-montant-comptant-${l.id}`}
            />
            <span className="text-muted-foreground">€ HT</span>
          </>
        )}

        <Label className="text-muted-foreground whitespace-nowrap shrink-0 ml-2">Remise</Label>
        <Input
          type="number"
          min={0}
          max={100}
          step={0.5}
          value={l.remise || ""}
          onChange={e => onUpdate(l.id, { remise: Number(e.target.value) })}
          className="h-7 w-16 tabular-nums text-right"
          data-testid={`input-remise-maint-${l.id}`}
        />
        <span className="text-muted-foreground">%</span>
        <div className="ml-auto font-semibold tabular-nums text-foreground">
          = {fmt(effectiveTotal)} €{!isComptant ? "/mois" : ""}
        </div>
      </div>
      {/* Description optionnelle */}
      <div className="flex items-start gap-2 text-xs pl-4 pt-1 border-t border-dashed border-border/40">
        <Label className="text-muted-foreground whitespace-nowrap shrink-0 pt-2">Description</Label>
        <Textarea
          rows={2}
          placeholder="Description (optionnel)"
          value={l.descriptionFr || ""}
          onChange={e => onUpdate(l.id, { descriptionFr: e.target.value })}
          className="min-h-[56px] flex-1 resize-y text-xs"
          data-testid={`textarea-desc-maint-${l.id}`}
        />
      </div>
      {/* Précision optionnelle */}
      <div className="flex items-center gap-2 text-xs pl-4 pt-1 border-t border-dashed border-border/40">
        <Label className="text-muted-foreground whitespace-nowrap shrink-0">Précision (optionnel)</Label>
        <Textarea
          rows={2}
          placeholder="ex. BellaBot — poste accueil"
          value={l.precision || ""}
          onChange={e => onUpdate(l.id, { precision: e.target.value || null })}
          className="min-h-[56px] flex-1 resize-y text-xs"
          data-testid={`textarea-precision-maint-${l.id}`}
        />
      </div>
    </div>
  );
}

// ── LignesSection ─────────────────────────────────────────────────────────────

interface LignesSectionProps {
  lignes: LigneDevis[];
  section: "produits" | "prestations";
  onUpdate: (id: string, c: Partial<LigneDevis>) => void;
  onRemove: (id: string) => void;
  onMove: (id: string, dir: -1 | 1) => void;
  onAdd: () => void;
  onAddMaintenance?: () => void;
  onAddInstallation?: () => void;
}

function LignesSection({ lignes, onUpdate, onRemove, onMove, onAdd, onAddMaintenance, onAddInstallation }: LignesSectionProps) {
  return (
    <div className="space-y-2">
      {lignes.length === 0 && (
        <div className="border border-dashed rounded-xl h-16 flex items-center justify-center text-muted-foreground text-sm">
          Aucune ligne — cliquez sur "Ajouter"
        </div>
      )}
      {lignes.map((l, idx) => {
        if (l.dureeMaintenance !== undefined) {
          return (
            <MaintenanceLigneCard
              key={l.id}
              l={l}
              idx={idx}
              total={lignes.length}
              onUpdate={onUpdate}
              onRemove={onRemove}
              onMove={onMove}
            />
          );
        }
        return (
        <div key={l.id} className="border rounded-xl p-3 bg-card space-y-2">
          <div className="flex gap-2 items-start">
            {/* Photo */}
            {l.imageUrl ? (
              <img src={l.imageUrl} className="w-10 h-10 rounded-lg object-cover border shrink-0 mt-0.5" />
            ) : (
              <div className="w-10 h-10 rounded-lg bg-secondary border flex items-center justify-center shrink-0 mt-0.5">
                <Package className="w-4 h-4 text-muted-foreground" />
              </div>
            )}
            {/* SKU + Name */}
            <div className="flex-1 min-w-0 space-y-1">
              <div className="flex gap-1">
                <Input
                  value={l.sku || ""}
                  onChange={e => onUpdate(l.id, { sku: e.target.value })}
                  placeholder="SKU"
                  className="h-7 w-28 text-xs font-mono"
                  data-testid={`input-sku-${l.id}`}
                />
                <Input
                  value={l.nomFr}
                  onChange={e => onUpdate(l.id, { nomFr: e.target.value })}
                  placeholder="Nom du produit"
                  className="h-7 flex-1 text-sm font-medium"
                  data-testid={`input-nom-${l.id}`}
                />
              </div>
              <Textarea
                rows={2}
                value={l.descriptionFr || ""}
                onChange={e => onUpdate(l.id, { descriptionFr: e.target.value })}
                placeholder="Description / sous-titre (optionnel)"
                className="min-h-[56px] resize-y text-xs text-muted-foreground"
                data-testid={`textarea-desc-${l.id}`}
              />
              {Array.isArray(l.bullets) && l.bullets.length > 0 && (
                <div className="mt-1 pl-1 space-y-0.5">
                  {l.bullets.map((b, bi) => (
                    <div key={bi} className="flex items-start gap-1 text-[11px] text-muted-foreground">
                      <span className="mt-0.5 text-primary shrink-0">•</span>
                      <span>{b}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            {/* Controls */}
            <div className="flex flex-col gap-1 shrink-0">
              <button type="button" onClick={() => onMove(l.id, -1)} disabled={idx === 0} className="p-1 rounded hover:bg-secondary disabled:opacity-30">
                <ChevronUp className="w-3 h-3" />
              </button>
              <button type="button" onClick={() => onMove(l.id, 1)} className="p-1 rounded hover:bg-secondary">
                <ChevronDown className="w-3 h-3" />
              </button>
              <button type="button" onClick={() => onRemove(l.id)} className="p-1 rounded hover:bg-red-100 text-red-500">
                <Trash2 className="w-3 h-3" />
              </button>
            </div>
          </div>
          {/* Prix row */}
          <div className="flex flex-wrap gap-2 items-center text-xs pl-12">
            <Label className="text-muted-foreground whitespace-nowrap shrink-0">PU HT</Label>
            <Input
              type="number"
              min={0}
              step={0.01}
              value={l.puHt || ""}
              onChange={e => onUpdate(l.id, { puHt: Number(e.target.value) })}
              className="h-7 w-24 tabular-nums text-right"
              data-testid={`input-pu-${l.id}`}
            />
            <span className="text-muted-foreground">€</span>
            <Label className="text-muted-foreground whitespace-nowrap shrink-0 ml-2">Qté</Label>
            <Input
              type="number"
              min={1}
              value={l.quantite}
              onChange={e => onUpdate(l.id, { quantite: Number(e.target.value) })}
              className="h-7 w-16 tabular-nums text-center"
              data-testid={`input-qty-${l.id}`}
            />
            <Label className="text-muted-foreground whitespace-nowrap shrink-0 ml-2">Remise</Label>
            <Input
              type="number"
              min={0}
              max={100}
              step={0.5}
              value={l.remise || ""}
              onChange={e => onUpdate(l.id, { remise: Number(e.target.value) })}
              className="h-7 w-16 tabular-nums text-right"
              data-testid={`input-remise-${l.id}`}
            />
            <span className="text-muted-foreground">%</span>
            <div className="ml-auto font-semibold tabular-nums text-foreground">
              = {(Number(l.puHt) * Number(l.quantite) * (1 - Number(l.remise) / 100)).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
            </div>
          </div>
          {/* Paiement + Nature row */}
          <div className="flex flex-wrap gap-2 items-center text-xs pl-12 pt-1 border-t border-dashed border-border/50">
            <Label className="text-muted-foreground whitespace-nowrap shrink-0">Paiement</Label>
            <Select
              value={l.modePaiement === "finance" ? (l.dureeFinancement || "24") : "comptant"}
              onValueChange={(v) => onUpdate(l.id, {
                modePaiement: v === "comptant" ? "comptant" : "finance",
                dureeFinancement: v === "comptant" ? null : v as DureeFinancement,
              })}
            >
              <SelectTrigger className="h-7 w-28 text-xs" data-testid={`select-paiement-${l.id}`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="comptant">Comptant</SelectItem>
                {FINANCEMENT_DUREES.map(d => (
                  <SelectItem key={d} value={d}>{d} mois</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Label className="text-muted-foreground whitespace-nowrap shrink-0 ml-2">Nature</Label>
            {CAPITAL_FORCE_TYPES.has(l.produitType || "") ? (
              <span className="h-7 px-2 flex items-center text-xs border rounded-md bg-muted text-muted-foreground select-none" data-testid={`select-nature-${l.id}`}>
                Capital
              </span>
            ) : (
              <Select
                value={l.natureLigne ?? "capital"}
                onValueChange={(v) => onUpdate(l.id, { natureLigne: v as "capital" | "recurrent" })}
              >
                <SelectTrigger className="h-7 w-28 text-xs" data-testid={`select-nature-${l.id}`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="capital">Capital</SelectItem>
                  <SelectItem value="recurrent">Récurrent</SelectItem>
                </SelectContent>
              </Select>
            )}
          </div>
        </div>
        );
      })}
      <Button variant="outline" size="sm" onClick={onAdd} className="w-full border-dashed" data-testid="button-add-ligne">
        <Plus className="w-4 h-4 mr-2" /> Ajouter depuis le catalogue
      </Button>
      {onAddMaintenance && (
        <Button variant="outline" size="sm" onClick={onAddMaintenance} className="w-full border-dashed text-blue-700 hover:text-blue-800" data-testid="button-add-maintenance">
          <Shield className="w-4 h-4 mr-2" /> Ajouter un contrat de maintenance
        </Button>
      )}
      {onAddInstallation && (
        <Button variant="outline" size="sm" onClick={onAddInstallation} className="w-full border-dashed text-green-700 hover:text-green-800" data-testid="button-add-installation">
          <Wrench className="w-4 h-4 mr-2" /> Ajouter un forfait installation
        </Button>
      )}
    </div>
  );
}
